/**
 * Cliente HTTP tipado.
 *
 * Renova o access token automaticamente quando ele expira: com TTL de 15 minutos, uma
 * sessão de edição de uma hora expiraria quatro vezes — e perder uma edição por isso
 * seria inaceitável.
 */
const TOKEN_KEY = 'easyedit.tokens'

export interface Tokens {
  accessToken: string
  refreshToken: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

let tokens: Tokens | null = null
let refreshing: Promise<Tokens | null> | null = null
const listeners = new Set<(tokens: Tokens | null) => void>()

export function loadTokens(): Tokens | null {
  if (tokens) return tokens
  try {
    const raw = localStorage.getItem(TOKEN_KEY)
    tokens = raw ? (JSON.parse(raw) as Tokens) : null
  } catch {
    tokens = null
  }
  return tokens
}

export function setTokens(next: Tokens | null): void {
  tokens = next
  try {
    if (next) localStorage.setItem(TOKEN_KEY, JSON.stringify(next))
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    // modo privado: a sessão vive só nesta aba
  }
  listeners.forEach((fn) => fn(next))
}

export function onAuthChange(fn: (tokens: Tokens | null) => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function accessToken(): string | null {
  return loadTokens()?.accessToken ?? null
}

async function refresh(): Promise<Tokens | null> {
  // Uma única renovação por vez: dez requisições expirando juntas não podem disparar dez
  // refreshes, porque a rotação do token invalidaria os nove seguintes.
  if (refreshing) return refreshing
  const current = loadTokens()
  if (!current?.refreshToken) return null

  refreshing = (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      })
      if (!response.ok) {
        setTokens(null)
        return null
      }
      const data = (await response.json()) as Tokens
      setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken })
      return loadTokens()
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

export interface RequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  /** Interno: impede laço infinito de renovação. */
  retried?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = accessToken()
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  if (token) headers.authorization = `Bearer ${token}`

  const response = await fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (response.status === 401 && !options.retried) {
    const renewed = await refresh()
    if (renewed) return request<T>(path, { ...options, retried: true })
  }

  if (!response.ok) {
    let code = 'ERROR'
    let message = `Falha na requisição (${response.status})`
    let details: unknown
    try {
      const payload = (await response.json()) as { error?: { code?: string; message?: string; details?: unknown } }
      code = payload.error?.code ?? code
      message = payload.error?.message ?? message
      details = payload.error?.details
    } catch {
      // corpo não-JSON: mantém a mensagem genérica
    }
    throw new ApiError(response.status, code, message, details)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** Upload direto ao storage por URL assinada — os bytes não passam pela API. */
export async function uploadToSignedUrl(
  url: string,
  file: File | Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
  /**
   * Ler o ETag da resposta.
   *
   * Só o multipart precisa dele. Pedir sempre faz o navegador registrar um aviso de
   * "unsafe header" no console em TODO upload simples entre origens — ruído que esconde
   * os erros que importam.
   */
  needsEtag = false,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('content-type', contentType)
    // `fetch` não reporta progresso de upload; XHR sim, e um vídeo de 2 GB sem barra de
    // progresso é indistinguível de um travamento.
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(needsEtag ? (xhr.getResponseHeader('etag') ?? '') : '')
        return
      }
      reject(new ApiError(xhr.status, 'UPLOAD_FAILED', `Upload falhou (${xhr.status})`))
    }
    xhr.onerror = () => reject(new ApiError(0, 'NETWORK', 'Falha de rede durante o upload'))
    xhr.send(file)
  })
}
